import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Clear as ClearIcon,
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  Search as SearchIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  BULLETIN_FILTER_LABELS,
  SCHEDULE_FILTER_LABELS,
  UPCOMING_SOURCE_FILTER_LABELS
} from '../maintenance/utils/maintenanceUtils';
import {
  downloadDueReportHistoryCsv,
  downloadDueReportHistoryPdf,
  downloadDueReportCsv,
  downloadDueReportPdf,
  fetchDueReportPreviewData,
  fetchDueReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const TYPE_OPTIONS = [
  { value: 'INSPECTION', label: 'Inspection' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'OVERHAUL', label: 'TSO (Time Since Overhaul)' },
  { value: 'REPAIR', label: 'Defect' },
  { value: 'REPLACEMENT', label: 'Replacement' },
  { value: 'TSN', label: 'TSN (Time Since New)' },
  { value: 'TSI', label: 'TSI (Time Since Inspection)' },
  { value: 'TSP', label: 'TSP (Time Since Part)' },
  { value: 'SLL', label: 'SLL (Service Life Limit)' },
  { value: 'OTL', label: 'OTL (Operational Time Limit)' }
];

const SOURCE_OPTIONS = Object.entries(UPCOMING_SOURCE_FILTER_LABELS)
  .filter(([value]) => value !== 'ALL')
  .map(([value, label]) => ({ value, label }));

const SCHEDULE_OPTIONS = Object.entries(SCHEDULE_FILTER_LABELS)
  .filter(([value]) => value !== 'ALL')
  .map(([value, label]) => ({ value, label }));

const BULLETIN_OPTIONS = Object.entries(BULLETIN_FILTER_LABELS)
  .filter(([value]) => value !== 'ALL')
  .map(([value, label]) => ({ value, label }));

const DUE_WINDOW_OPTIONS = [
  { value: 'PAST_DUE_ONLY', label: 'Past Due Only' },
  { value: 'UPCOMING_ONLY', label: 'Upcoming Only' }
];

const DAYS_OPTIONS = [30, 60, 90, 120, 180, 240, 480];
const HOURS_OPTIONS = [25, 50, 100, 250, 500, 1000];
const CYCLES_OPTIONS = [100, 250, 500, 1000, 2500, 5000];
const LANDINGS_OPTIONS = [50, 100, 250, 500, 1000, 2500];

function normalizeSelectValues(value) {
  if (value == null) return [];
  return typeof value === 'string' ? value.split(',').filter(Boolean) : value;
}

/** Persist single string or multi array; empty = no restriction. */
function compactFilterSelection(selected) {
  if (!Array.isArray(selected) || selected.length === 0) return undefined;
  return selected.length === 1 ? selected[0] : selected;
}

function formatMultiSelectLabels(selected, labelByValue, emptyLabel) {
  if (!selected?.length) return emptyLabel;
  return selected.map((v) => labelByValue[v] || v).join(', ');
}

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function buildUpcomingFiltersPayload({
  search,
  types,
  sources,
  scheduleFilters,
  adsbBulletinTypeFilters,
  dueWindowFilters,
  rangeEnabled,
  days,
  hours,
  cycles,
  landings,
  hasCalendarRange = false
}) {
  const useDays = !!rangeEnabled?.days;
  const useHours = !!rangeEnabled?.hours;
  const useCycles = !!rangeEnabled?.cycles;
  const useLandings = !!rangeEnabled?.landings;
  // Same as Maintenance → Upcoming: no range checkboxes → unbounded show-all.
  // Calendar date range on Due Report also activates days mode.
  const daysMode = useDays || hasCalendarRange;
  const noRangeFilter = !daysMode && !useHours && !useCycles && !useLandings;

  const sourceList = Array.isArray(sources) ? sources : [];
  const isAdsbOnly = sourceList.length === 1 && sourceList[0] === 'ADSB_SERVICE_BULLETIN';
  const includesAdsb = sourceList.length === 0 || sourceList.includes('ADSB_SERVICE_BULLETIN');

  const filters = {
    search: search.trim() || undefined,
    source: compactFilterSelection(sourceList) || 'ALL',
    scheduleFilter: isAdsbOnly
      ? 'ALL'
      : (compactFilterSelection(scheduleFilters) || 'ALL'),
    adsbBulletinTypeFilter: includesAdsb
      ? (compactFilterSelection(adsbBulletinTypeFilters) || 'ALL')
      : 'ALL',
    // Due Window only applies when Days is on (or a calendar range is set).
    dueWindowFilter: daysMode
      ? (compactFilterSelection(dueWindowFilters) || 'ALL')
      : 'ALL',
    useDays: daysMode || noRangeFilter ? '1' : '0',
    useHours: useHours ? '1' : '0',
    useCycles: useCycles ? '1' : '0',
    useLandings: useLandings ? '1' : '0',
    hours: String(hours || 100),
    cycles: String(cycles || 500),
    landings: String(landings || 250),
    sortBy: 'dueDate',
    sortOrder: 'asc'
  };

  if (noRangeFilter) {
    filters.unbounded = '1';
  } else {
    // Same as useMaintenanceData: any explicit range sets pastDays=90.
    filters.pastDays = '90';
    if (useDays) {
      filters.days = String(typeof days === 'number' ? days : 90);
    }
  }

  const typeValue = compactFilterSelection(types);
  if (typeValue) {
    filters.type = typeValue;
  }

  return filters;
}

/**
 * Dedicated Due Report flow: multi-aircraft + Upcoming filters + date range generate,
 * plus download history.
 */
const DueReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [scheduleFilters, setScheduleFilters] = useState([]);
  const [adsbBulletinTypeFilters, setAdsbBulletinTypeFilters] = useState([]);
  const [dueWindowFilters, setDueWindowFilters] = useState([]);
  const [rangeEnabled, setRangeEnabled] = useState({
    days: false,
    hours: false,
    cycles: false,
    landings: false
  });
  const [days, setDays] = useState(90);
  const [hours, setHours] = useState(100);
  const [cycles, setCycles] = useState(500);
  const [landings, setLandings] = useState(250);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const showBulletinFilter = sources.length === 0 || sources.includes('ADSB_SERVICE_BULLETIN');
  const showScheduleFilter = sources.length === 0 || sources.some((s) => s !== 'ADSB_SERVICE_BULLETIN');
  const typeLabelByValue = useMemo(
    () => Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label])),
    []
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchDueReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const filtersPayload = useMemo(
    () => buildUpcomingFiltersPayload({
      search,
      types,
      sources,
      scheduleFilters,
      adsbBulletinTypeFilters,
      dueWindowFilters,
      rangeEnabled,
      days,
      hours,
      cycles,
      landings,
      hasCalendarRange: Boolean(dateFrom || dateTo)
    }),
    [
      search,
      types,
      sources,
      scheduleFilters,
      adsbBulletinTypeFilters,
      dueWindowFilters,
      rangeEnabled,
      days,
      hours,
      cycles,
      landings,
      dateFrom,
      dateTo
    ]
  );

  const requestBody = useMemo(
    () => ({
      aircraftIds: selectedAircraftIds,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      reportDate,
      filters: filtersPayload
    }),
    [selectedAircraftIds, dateFrom, dateTo, reportDate, filtersPayload]
  );

  const toggleRangeEnabled = (key) => {
    setRangeEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSourcesChange = (value) => {
    const next = normalizeSelectValues(value);
    setSources(next);
    const isAdsbOnly = next.length === 1 && next[0] === 'ADSB_SERVICE_BULLETIN';
    const includesAdsb = next.length === 0 || next.includes('ADSB_SERVICE_BULLETIN');
    if (isAdsbOnly) {
      setScheduleFilters([]);
    }
    if (!includesAdsb) {
      setAdsbBulletinTypeFilters([]);
    }
  };

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchDueReportPreviewData(requestBody);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchDueReportPreviewData(requestBody);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadDueReportPdf(
        { ...requestBody, visualizations },
        `Due_Report_${dateFrom || 'all'}_to_${dateTo || 'all'}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Due Report with visualizations downloaded and saved to history'
          : 'Due Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadDueReportCsv(
        requestBody,
        `Due_Report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`
      );
      showToast('Due Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadDueReportHistoryPdf(row._id, row.filename || 'Due_Report.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadDueReportHistoryCsv(row._id, row.filename || 'Due_Report.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Due Report
      </Typography>
      <Typography color="text.secondary" paragraph>
        Upcoming and past-due maintenance items (inspections, services, TSR, AMP, AD/SB) for selected aircraft.
        All filters are optional — leave them blank to include every due item (no day-window limit).
        Each download is stored so you can re-download it later.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <FormControl fullWidth>
              <InputLabel id="due-report-aircrafts-label">Aircrafts</InputLabel>
              <Select
                labelId="due-report-aircrafts-label"
                multiple
                value={selectedAircraftIds}
                label="Aircrafts"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAircraftIds(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All aircraft';
                  return selected.map((id) => aircraftLabelById[id] || id).join(', ');
                }}
              >
                {aircraftOptions.map((ac) => (
                  <MenuItem key={ac.value} value={ac.value}>
                    {ac.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Optional — leave empty for all aircraft
              </Typography>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Due date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
              <TextField
                label="Due date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            <Typography variant="subtitle2">Upcoming filters (optional)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    multiple
                    value={types}
                    label="Type"
                    onChange={(e) => setTypes(normalizeSelectValues(e.target.value))}
                    renderValue={(selected) => formatMultiSelectLabels(selected, typeLabelByValue, 'All Types')}
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox size="small" checked={types.includes(opt.value)} />
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Source</InputLabel>
                  <Select
                    multiple
                    value={sources}
                    label="Source"
                    onChange={(e) => handleSourcesChange(e.target.value)}
                    renderValue={(selected) => formatMultiSelectLabels(
                      selected,
                      UPCOMING_SOURCE_FILTER_LABELS,
                      'All Sources'
                    )}
                  >
                    {SOURCE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox size="small" checked={sources.includes(opt.value)} />
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {showBulletinFilter && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Bulletin filter</InputLabel>
                    <Select
                      multiple
                      value={adsbBulletinTypeFilters}
                      label="Bulletin filter"
                      onChange={(e) => setAdsbBulletinTypeFilters(normalizeSelectValues(e.target.value))}
                      renderValue={(selected) => formatMultiSelectLabels(
                        selected,
                        BULLETIN_FILTER_LABELS,
                        'All'
                      )}
                    >
                      {BULLETIN_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox size="small" checked={adsbBulletinTypeFilters.includes(opt.value)} />
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {showScheduleFilter && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Schedule filter</InputLabel>
                    <Select
                      multiple
                      value={scheduleFilters}
                      label="Schedule filter"
                      onChange={(e) => setScheduleFilters(normalizeSelectValues(e.target.value))}
                      renderValue={(selected) => formatMultiSelectLabels(
                        selected,
                        SCHEDULE_FILTER_LABELS,
                        'All'
                      )}
                    >
                      {SCHEDULE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox size="small" checked={scheduleFilters.includes(opt.value)} />
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" disabled={!rangeEnabled.days && !dateFrom && !dateTo}>
                  <InputLabel>Due Window</InputLabel>
                  <Select
                    multiple
                    value={dueWindowFilters}
                    label="Due Window"
                    onChange={(e) => setDueWindowFilters(normalizeSelectValues(e.target.value))}
                    renderValue={(selected) => formatMultiSelectLabels(
                      selected,
                      {
                        PAST_DUE_ONLY: 'Past Due Only',
                        UPCOMING_ONLY: 'Upcoming Only'
                      },
                      'All (Past Due + Upcoming)'
                    )}
                  >
                    {DUE_WINDOW_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox size="small" checked={dueWindowFilters.includes(opt.value)} />
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!rangeEnabled.days}
                      onChange={() => toggleRangeEnabled('days')}
                    />
                  }
                  label="Days range"
                />
                <FormControl fullWidth size="small" disabled={!rangeEnabled.days}>
                  <InputLabel>Show Next (Days)</InputLabel>
                  <Select
                    value={days}
                    label="Show Next (Days)"
                    onChange={(e) => setDays(e.target.value)}
                  >
                    {DAYS_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n} Days</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!rangeEnabled.hours}
                      onChange={() => toggleRangeEnabled('hours')}
                    />
                  }
                  label="Hours range"
                />
                <FormControl fullWidth size="small" disabled={!rangeEnabled.hours}>
                  <InputLabel>Show Next (Hours)</InputLabel>
                  <Select
                    value={hours}
                    label="Show Next (Hours)"
                    onChange={(e) => setHours(e.target.value)}
                  >
                    {HOURS_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n} Hours</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!rangeEnabled.cycles}
                      onChange={() => toggleRangeEnabled('cycles')}
                    />
                  }
                  label="Cycles range"
                />
                <FormControl fullWidth size="small" disabled={!rangeEnabled.cycles}>
                  <InputLabel>Show Next (Cycles)</InputLabel>
                  <Select
                    value={cycles}
                    label="Show Next (Cycles)"
                    onChange={(e) => setCycles(e.target.value)}
                  >
                    {CYCLES_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n} Cycles</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!rangeEnabled.landings}
                      onChange={() => toggleRangeEnabled('landings')}
                    />
                  }
                  label="Landings range"
                />
                <FormControl fullWidth size="small" disabled={!rangeEnabled.landings}>
                  <InputLabel>Show Next (Landings)</InputLabel>
                  <Select
                    value={landings}
                    label="Show Next (Landings)"
                    onChange={(e) => setLandings(e.target.value)}
                  >
                    {LANDINGS_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n} Landings</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Report date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Shown as the report date on the Prepared By section"
              sx={{ maxWidth: 640 }}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded Due Reports</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No Due Report downloads yet. Generate one from the Generate tab.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Aircrafts</TableCell>
                  <TableCell>Due date range</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {(row.aircraftLabels || []).length
                        ? row.aircraftLabels.join(', ')
                        : 'All aircraft'}
                    </TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : 'No date filter'}
                    </TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Due Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Due Report"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default DueReportPanel;
